import DeleteOutlineOutlinedIcon from '@mui/icons-material/DeleteOutlineOutlined';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import { Stack, Typography } from '@mui/material';
import ProductBroken from 'app/product/component/ProductBroken';
import useColorsStyles from 'common/colors';
import CustomIconButton from 'common/components/CustomIconButton';
import CustomTooltip from 'common/components/CustomTooltip';
import AssetType from 'common/enums/assetTypes';
import StickerType from 'common/enums/stickersTypes';
import useFlexBoxStyles from 'common/flexBox';
import useFontStyles from 'common/fontStyles';
import useOnliveStyles from 'common/onliveStyles';
import { getAssetImage } from 'common/utils/assetsImage';
import { applyDiscount, hasDiscount } from 'common/utils/productDiscount';
import getSymbolFromCurrency from 'currency-symbol-map';
import { useTranslation } from 'react-i18next';
import { NumericFormat } from 'react-number-format';
import { makeStyles } from 'tss-react/mui';

const useStyles = makeStyles()((theme) => ({
    typeChip: {
        minWidth: theme.spacing(5),
    },
    actionAsset: {
        top: theme.spacing(1.5),
        right: theme.spacing(1.5),
    },
    selectAssetItemImage: {
        minWidth: theme.spacing(13),
        height: theme.spacing(13),
        '& > img': {
            height: '100%',
            width: 'auto',
        },
        [theme.breakpoints.up('xl')]: {
            height: theme.spacing(20),
        },
    },
    quiz: {
        color: '#FFB3A0',
    },
    survey: {
        color: '#89C6F4',
    },
    multimedia: {
        height: theme.spacing(18),
        '& video': {
            maxHeight: theme.spacing(18),
            backgroundColor: 'black',
            width: '100%',
        },
        '& img': {
            maxHeight: theme.spacing(18),
            width: 'auto',
        },
    },
    scaleStickers: {
        transform: 'scale(0.6)',
        [theme.breakpoints.up('xl')]: {
            transform: 'scale(0.5)',
        },
    },
    scaleDiscount: {
        transform: 'scale(0.8)',
        [theme.breakpoints.up('xl')]: {
            transform: 'scale(1)',
        },
    },
    medium: {
        fontSize: theme.spacing(9),
        [theme.breakpoints.up('xl')]: {
            fontSize: theme.spacing(12),
        },
    },
    big: {
        fontSize: theme.spacing(8),
        [theme.breakpoints.up('xl')]: {
            fontSize: theme.spacing(10),
        },
    },
}));

const EventFormAssetsItem = (props) => {
    const { onClick, enableOnClick, asset, deleteAsset, deleteButton, editAsset, editButton, key } = props;

    const { t } = useTranslation();
    const { classes, cx } = useStyles();

    const { classes: flexBox } = useFlexBoxStyles();
    const { classes: colors } = useColorsStyles();
    const { classes: onlive } = useOnliveStyles();
    const { classes: font } = useFontStyles();

    const handleOnClick = () => {
        if (enableOnClick) {
            onClick();
        }
    };

    const getNameFromType = () => {
        switch (asset.type) {
            case AssetType.QUIZ:
                return t('chipTypeLabel.quiz');
            case AssetType.SURVEY:
                return t('chipTypeLabel.survey');
            case AssetType.AUCTION:
                return t('chipTypeLabel.auction');
            case AssetType.MULTIMEDIA:
                if (asset.format.startsWith('image')) return t('chipTypeLabel.image');
                if (asset.format.startsWith('video')) return t('chipTypeLabel.video');
                if (asset.format.startsWith('application') && asset.format !== 'application/immersive')
                    return t('chipTypeLabel.presentation');
                if (asset.format === 'application/immersive') return t('chipTypeLabel.immersive');
                return '';
            case AssetType.CALL_TO_ACTION:
                return t('chipTypeLabel.cta');
            case AssetType.STICKER:
                return t('chipTypeLabel.sticker');
            default:
                return '';
        }
    };

    const getChip = () => {
        if (asset.type !== AssetType.PRODUCT && asset.type !== AssetType.DISCOUNT) {
            return (
                <Stack
                    className={cx(
                        classes.typeChip,
                        colors.bgBlack,
                        colors.textWhite,
                        flexBox.padding05,
                        flexBox.absolute,
                        flexBox.bottom1,
                        flexBox.left0,
                        flexBox.textAlignCenter,
                    )}
                >
                    <Typography variant="caption" fontWeight={'bold'}>
                        {getNameFromType().toUpperCase()}
                    </Typography>
                </Stack>
            );
        }
        return null;
    };

    const getImageClass = () => {
        switch (asset.type) {
            case AssetType.SURVEY:
                return cx(classes.survey, classes.medium, flexBox.flex, flexBox.alignItemsCenter);
            case AssetType.QUIZ:
                return cx(classes.quiz, classes.medium, flexBox.flex, flexBox.alignItemsCenter);

            case AssetType.CALL_TO_ACTION:
                if (asset.callToActionType === AssetType.PROMOTION) {
                    return '';
                }
                return cx(classes.scaleStickers, flexBox.flex);

            case AssetType.STICKER:
                if (asset.stickerType === StickerType.POSTER) {
                    return cx(classes.scaleStickers, flexBox.flex);
                }
                return cx(classes.big, flexBox.flex, flexBox.h100, flexBox.wAuto, flexBox.alignItemsCenter);
            case AssetType.MULTIMEDIA:
                return cx(
                    classes.multimedia,
                    flexBox.relative,
                    flexBox.flex,
                    flexBox.alignItemsCenter,
                    flexBox.margin05,
                    flexBox.justifyContentCenter,
                    classes.medium,
                    flexBox.flex,
                    flexBox.alignItemsCenter,
                );
            case AssetType.DISCOUNT:
                return cx(classes.scaleDiscount, flexBox.flex);
            default:
                return cx(classes.big, flexBox.flex, flexBox.h100, flexBox.wAuto, flexBox.alignItemsCenter);
        }
    };

    return (
        <Stack
            direction="column"
            alignItems="center"
            spacing={0}
            key={key}
            className={cx(flexBox.relative, colors.bgPaper, flexBox.padding1)}
        >
            {deleteButton && (
                <Stack
                    className={cx(
                        classes.actionAsset,
                        flexBox.absolute,
                        flexBox.borderRadius50,
                        flexBox.zIndex2,
                        colors.bgPaper,
                    )}
                >
                    <CustomTooltip arrow title={t('event.eventItem.delete')}>
                        <CustomIconButton size="small" onClick={() => deleteAsset(asset)}>
                            <DeleteOutlineOutlinedIcon />
                        </CustomIconButton>
                    </CustomTooltip>
                </Stack>
            )}

            {asset.type === 'product' ? (
                <ProductBroken product={asset} size="small" className={flexBox.margin05} />
            ) : null}

            {editButton && (
                <Stack
                    className={cx(
                        classes.actionAsset,
                        flexBox.absolute,
                        flexBox.borderRadius50,
                        flexBox.zIndex2,
                        colors.bgPaper,
                    )}
                >
                    <CustomTooltip arrow title={t('assetItem.edit')}>
                        <CustomIconButton size="small" onClick={() => editAsset(asset)}>
                            <EditOutlinedIcon />
                        </CustomIconButton>
                    </CustomTooltip>
                </Stack>
            )}
            <Stack
                direction="column"
                justifyContent="center"
                alignItems="center"
                aria-hidden={true}
                className={cx(
                    flexBox.overflowHidden,
                    flexBox.relative,
                    flexBox.w100,
                    flexBox.borderRadius2,
                    classes.selectAssetItemImage,
                    onlive.borderMediumGray,
                    enableOnClick && flexBox.cursorPointer,
                )}
                onClick={handleOnClick}
            >
                {getAssetImage(asset, getImageClass())}

                {getChip()}
            </Stack>
            <Stack
                direction="column"
                className={cx(classes.selectAssetItemName, flexBox.maxW100, flexBox.h50, flexBox.w100)}
            >
                <CustomTooltip arrow title={asset.name} aria-label="asset item" placement="bottom">
                    <Typography
                        className={cx(
                            font.clamp,
                            flexBox.overflowHidden,
                            font.clampLine2,
                            flexBox.paddingTop05,
                            flexBox.margin005,
                            font.overflowWrap,
                        )}
                    >
                        {asset.name}
                    </Typography>
                </CustomTooltip>

                {asset.type === 'product' && (
                    <Stack direction="column" className={flexBox.margin005}>
                        <Typography fontWeight={500}>
                            <NumericFormat
                                value={applyDiscount(asset)}
                                displayType={'text'}
                                thousandSeparator={true}
                                prefix={getSymbolFromCurrency(asset.currency)}
                            />
                        </Typography>
                        {hasDiscount(asset) && (
                            <Typography fontWeight={500} className={classes.wrongText}>
                                <NumericFormat
                                    value={asset.price}
                                    displayType={'text'}
                                    thousandSeparator={true}
                                    prefix={getSymbolFromCurrency(asset.currency)}
                                />
                            </Typography>
                        )}
                    </Stack>
                )}
            </Stack>
        </Stack>
    );
};

export default EventFormAssetsItem;
